import { isDefinedObject, isDefinedString, reduce } from "et3-type-guards";
import { isEmpty, memoize, orderBy } from "lodash";
import { observable, runInAction } from "mobx";
import queryString from "query-string";

import { apiCallGetConversation } from "../../../api/user-api/api-call-get-conversation";
import {
  getUniqueConversationData,
  parseConversationData
} from "../../../helpers/et3-conversation-parse";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import {
  E_MESSAGE_DELETION_STATUS,
  Gender,
  TSeachedUser
} from "../../../Types";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class ConversationStore {
  senderIdInput = observable.box<number>(-1);
  recipientIdInput = observable.box<number>(-1);
  messagesLimit = observable.box<TEt3PickerSingleValue>({
    label: "50",
    value: "50"
  });

  searchedUser = observable.box<TSeachedUser>({
    userID: -1,
    userGender: ""
  });
  conversationData = observable.box({
    conversation: [],
    senderUsername: "",
    recipientUsername: ""
  });
  messageEra = observable.box<TEt3PickerSingleValue>({
    label: "POST_2025",
    value: "POST_2025"
  });
  setMessageEra = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.messageEra.set(limit);
    });
  };
  setSenderIdInput = (userID: number) => {
    this.senderIdInput.set(userID);
  };
  setRecipientIdInput = (userID: number) => {
    this.recipientIdInput.set(userID);
  };
  setMessagesLimit = (limit: TEt3PickerSingleValue) => {
    this.messagesLimit.set(limit);
  };
  checkURL = async (location: any) => {
    const search = reduce(() => location.search, "");
    if (isDefinedString(search)) {
      const senderID = queryString.parse(search).senderID;
      const recipientID = queryString.parse(search).recipientID;
      if (isDefinedString(senderID) && isDefinedString(recipientID)) {
        const senderIDNumber = parseInt(senderID, 10);
        const recipientIDNumber = parseInt(recipientID, 10);
        this.setSenderIdInput(senderIDNumber);
        this.setRecipientIdInput(recipientIDNumber);
        await this.getConversation({ isLoadMore: false });
      }
    }
  };
  getConversation = async ({ isLoadMore }: { isLoadMore: boolean }) => {
    const senderId = this.senderIdInput.get();
    const recipientId = this.recipientIdInput.get();
    let oldConversation = [];
    let limit = reducerToZeroIfUndefined(
      parseInt(this.messagesLimit.get().value, 10)
    );
    let offset = 0;
    if (isLoadMore) {
      limit = 500;
      oldConversation = this.conversationData.get().conversation;
      offset = oldConversation.length;
    }
    getCoreUIStore().setIsLoading("USER_CONVERSATIONS", true);
    const { data, status, code } = await apiCallGetConversation({
      senderId,
      recipientId,
      limit,
      offset,
      messageEra: this.messageEra.get().value
    });
    if (status === "OK" && isDefinedObject(data)) {
      const definedConversation = reduce(
        () => data.conversation,
        [
          {
            id: -1,
            messageId: -1,
            senderId: -1,
            recipientId: -1,
            message: "",
            sentOn: "",
            messageType: "",
            isDeleted: E_MESSAGE_DELETION_STATUS.NOT_DELETED,
            deletedOn: "",
            reactions: [],
            hasReactionHistory: false
          }
        ]
      );
      const firstData = reduce(() => definedConversation[0], {
        id: -1,
        messageId: -1,
        senderId: -1,
        recipientId: -1,
        message: "",
        sentOn: "",
        messageType: "",
        isDeleted: E_MESSAGE_DELETION_STATUS.NOT_DELETED,
        deletedOn: "",
        reactions: [],
        hasReactionHistory: false
      });
      if (
        isDefinedObject(firstData) &&
        !isEmpty(firstData) &&
        firstData.messageId > 0
      ) {
        const newConversation = parseConversationData(definedConversation);

        const conv = oldConversation.concat(newConversation);

        const uniqueConversation = getUniqueConversationData({
          data: conv,
          uniqueBy: "messageId"
        });
        const sortedData = orderBy(uniqueConversation, "sentOn", "desc");

        const newData = {
          senderUsername: data.senderUsername,
          recipientUsername: data.recipientUsername,
          conversation: sortedData
        };
        this.conversationData.set(newData);
        this.searchedUser.set(data.searchedUser);
      }
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("USER_CONVERSATIONS", false);
  };

  getMessagesData = () => {
    const converationData = getUserConversationStore().conversationData.get();
    const { conversation, senderUsername, recipientUsername } = converationData;
    const searchedUser = this.searchedUser.get();
    return conversation.map((message) => {
      const isMale = searchedUser.userGender === Gender.Male;
      const isSenderID = searchedUser.userID === message.senderId;
      const senderIdGender = isSenderID
        ? searchedUser.userGender
        : isMale
          ? Gender.Female
          : Gender.Male;
      return {
        ...message,
        senderUsername: senderUsername,
        recipientUsername: recipientUsername,
        gender: senderIdGender,
        message: message.messageObject.message,
        messageType: message.messageObject.messageType
      };
    });
  };
}

const getUserConversationStore = memoize(
  () => new ConversationStore(),
  () => 1
);
export default getUserConversationStore;
